import React, { useState, useEffect } from 'react';
import phoneRulesService, { BlockedNumberEntry } from '../services/phoneRulesService';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CharCounter } from "@/components/ui/char-counter";
import { BUDGET_MESSAGE_MAX } from "@/constants/contentLimits";
import { AlertCircle, CheckCircle2, Loader2, Volume2 } from "lucide-react";
import { toast } from "sonner";

function formatPrefixes(prefixes: string[]) {
  if (!prefixes || prefixes.length === 0) return '';
  return prefixes.map(prefix => `+${prefix}`).join(', ');
}

function parsePrefixInput(value: string): string[] {
  if (!value) return [];
  const parts = value.split(',').map(p => p.trim()).filter(Boolean);
  const normalized: string[] = [];
  const seen = new Set<string>();
  for (const part of parts) {
    const digits = part.replace(/\D/g, '');
    if (!digits) continue;
    if (digits.length < 1 || digits.length > 4) {
      throw new Error('Ländervorwahlen müssen 1-4 Ziffern lang sein.');
    }
    if (!seen.has(digits)) {
      seen.add(digits);
      normalized.push(digits);
    }
  }
  return normalized;
}

function normalizeDigits(value: string) {
  return value.replace(/\D/g, '');
}

interface AgentRulesSectionProps {
  /** Agent these limits belong to. null = the company-wide limits. */
  agentNumber?: string | null;
}

const AgentRulesSection: React.FC<AgentRulesSectionProps> = ({ agentNumber = null }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allowedPrefixesInput, setAllowedPrefixesInput] = useState('');
  const [blockedPrefixesInput, setBlockedPrefixesInput] = useState('');
  const [blockedNumbers, setBlockedNumbers] = useState<BlockedNumberEntry[]>([]);
  const [newBlockedNumber, setNewBlockedNumber] = useState('');
  const [monthlyBudgetInput, setMonthlyBudgetInput] = useState('');
  const [currentMonthCost, setCurrentMonthCost] = useState(0);
  const [budgetExceeded, setBudgetExceeded] = useState(false);
  const [budgetWhatsappText, setBudgetWhatsappText] = useState('');
  const [budgetVoiceText, setBudgetVoiceText] = useState('');
  const [hasVoiceAudio, setHasVoiceAudio] = useState(false);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const rules = await phoneRulesService.getRules(agentNumber);
        setAllowedPrefixesInput(formatPrefixes(rules.allowed_country_prefixes || []));
        setBlockedPrefixesInput(formatPrefixes(rules.blocked_country_prefixes || []));
        setBlockedNumbers(rules.blocked_numbers || []);
        setMonthlyBudgetInput(rules.monthly_budget != null ? String(rules.monthly_budget) : '');
        setCurrentMonthCost(rules.current_month_cost ?? 0);
        setBudgetExceeded(rules.budget_exceeded ?? false);
        setBudgetWhatsappText(rules.budget_exceeded_whatsapp_text ?? '');
        setBudgetVoiceText(rules.budget_exceeded_voice_text ?? '');
        setHasVoiceAudio(rules.has_voice_audio ?? false);
      } catch (err) {
        console.error('Error fetching phone rules:', err);
      } finally {
        setLoading(false);
      }
    })();
    // Reload when the page switches to another agent — its limits differ.
  }, [agentNumber]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const allowed = parsePrefixInput(allowedPrefixesInput);
      const blocked = parsePrefixInput(blockedPrefixesInput);
      const budgetValue = monthlyBudgetInput.trim() === '' ? null : parseFloat(monthlyBudgetInput);
      if (budgetValue !== null && (isNaN(budgetValue) || budgetValue < 0)) {
        toast.error('Budget muss eine positive Zahl sein oder leer für kein Limit.');
        setSaving(false);
        return;
      }
      const updated = await phoneRulesService.updateRules(
        {
          allowed_country_prefixes: allowed,
          blocked_country_prefixes: blocked,
          monthly_budget: budgetValue,
          budget_exceeded_whatsapp_text: budgetWhatsappText.trim() || null,
          budget_exceeded_voice_text: budgetVoiceText.trim() || null,
        },
        agentNumber,
      );
      setAllowedPrefixesInput(formatPrefixes(updated.allowed_country_prefixes || []));
      setBlockedPrefixesInput(formatPrefixes(updated.blocked_country_prefixes || []));
      setBlockedNumbers(updated.blocked_numbers || []);
      setMonthlyBudgetInput(updated.monthly_budget != null ? String(updated.monthly_budget) : '');
      setCurrentMonthCost(updated.current_month_cost ?? 0);
      setBudgetExceeded(updated.budget_exceeded ?? false);
      setBudgetWhatsappText(updated.budget_exceeded_whatsapp_text ?? '');
      setBudgetVoiceText(updated.budget_exceeded_voice_text ?? '');
      setHasVoiceAudio(updated.has_voice_audio ?? false);
      toast.success('Agenten-Regeln gespeichert');
    } catch (err) {
      console.error('Error updating phone rules:', err);
      toast.error(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateAudio = async () => {
    if (!budgetVoiceText.trim()) {
      toast.error('Bitte erst einen Sprachtext eingeben.');
      return;
    }
    setGeneratingAudio(true);
    try {
      const blob = await phoneRulesService.generateBudgetAudio(budgetVoiceText.trim());
      const url = URL.createObjectURL(blob);
      if (audioPreviewUrl) URL.revokeObjectURL(audioPreviewUrl);
      setAudioPreviewUrl(url);
      setHasVoiceAudio(true);
      toast.success('Audio generiert und gespeichert.');
    } catch (err) {
      console.error('Error generating audio:', err);
      toast.error('Audio konnte nicht generiert werden.');
    } finally {
      setGeneratingAudio(false);
    }
  };

  const handleAddBlockedNumber = async () => {
    const digits = normalizeDigits(newBlockedNumber);
    if (!digits || digits.length < 7 || digits.length > 15) {
      toast.error('Bitte eine gültige Telefonnummer angeben.');
      return;
    }
    setSaving(true);
    try {
      const entry = await phoneRulesService.addBlockedNumber(
        newBlockedNumber.trim(),
        undefined,
        agentNumber,
      );
      setBlockedNumbers(prev => [entry, ...prev.filter(item => item.id !== entry.id)]);
      setNewBlockedNumber('');
      toast.success('Nummer zur Blockliste hinzugefügt');
    } catch (err) {
      console.error('Error adding blocked number:', err);
      toast.error('Nummer konnte nicht blockiert werden');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveBlockedNumber = async (id: string) => {
    setSaving(true);
    try {
      await phoneRulesService.removeBlockedNumber(id);
      setBlockedNumbers(prev => prev.filter(item => item.id !== id));
      toast.success('Nummer entfernt');
    } catch (err) {
      console.error('Error removing blocked number:', err);
      toast.error('Nummer konnte nicht entfernt werden');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Monatsbudget */}
      <div className="space-y-3">
        <Label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
          Monatsbudget
        </Label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          <div className="space-y-2">
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-sm text-muted-foreground">€</span>
              <Input
                type="number"
                min="0"
                step="1"
                value={monthlyBudgetInput}
                onChange={(e) => setMonthlyBudgetInput(e.target.value)}
                placeholder="Kein Limit"
                className="bg-muted border-border pl-8"
                disabled={loading}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Maximales Budget pro Monat in Euro. Leer lassen für kein Limit.
            </p>
          </div>
          <div className="space-y-2">
            {monthlyBudgetInput.trim() !== '' && (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Aktueller Verbrauch</span>
                  <span className={`font-semibold ${budgetExceeded ? 'text-destructive-strong' : 'text-muted-foreground'}`}>
                    {currentMonthCost.toFixed(2)} € / {parseFloat(monthlyBudgetInput || '0').toFixed(2)} €
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${budgetExceeded ? 'bg-destructive' : 'bg-[#1967D2]'}`}
                    style={{ width: `${Math.min((currentMonthCost / (parseFloat(monthlyBudgetInput) || 1)) * 100, 100)}%` }}
                  />
                </div>
                {budgetExceeded && (
                  <Alert className="border-destructive/30 bg-destructive/10">
                    <AlertCircle className="h-4 w-4 text-destructive-strong" />
                    <AlertDescription className="text-destructive-strong text-xs">
                      Budget erreicht - Telefon & WhatsApp sind bis zum nächsten Monat pausiert.
                    </AlertDescription>
                  </Alert>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Budget-Nachrichten */}
      <div className="space-y-4 border-t border-border pt-4">
        <Label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
          Budget-Nachrichten
        </Label>

        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground">WhatsApp-Nachricht bei Budget-Überschreitung</Label>
          <Textarea
            value={budgetWhatsappText}
            onChange={(e) => setBudgetWhatsappText(e.target.value)}
            placeholder="Unser monatliches Gesprächsbudget ist erschöpft. Bitte versuchen Sie es nächsten Monat erneut."
            className="bg-muted border-border resize-none"
            rows={3}
            maxLength={BUDGET_MESSAGE_MAX}
            disabled={loading}
          />
          <CharCounter value={budgetWhatsappText} max={BUDGET_MESSAGE_MAX} />
          <p className="text-xs text-muted-foreground">
            Wird als WhatsApp-Antwort gesendet wenn das Budget überschritten ist. Leer lassen für keine Antwort.
          </p>
        </div>

        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground">Sprachansage bei Budget-Überschreitung</Label>
          <Textarea
            value={budgetVoiceText}
            onChange={(e) => setBudgetVoiceText(e.target.value)}
            placeholder="Leider ist unser monatliches Gesprächsbudget erschöpft. Bitte rufen Sie nächsten Monat wieder an. Auf Wiederhören."
            className="bg-muted border-border resize-none"
            rows={3}
            maxLength={BUDGET_MESSAGE_MAX}
            disabled={loading}
          />
          <CharCounter value={budgetVoiceText} max={BUDGET_MESSAGE_MAX} />
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handleGenerateAudio}
              disabled={generatingAudio || loading || !budgetVoiceText.trim()}
            >
              {generatingAudio ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Volume2 className="h-4 w-4 mr-2" />}
              {hasVoiceAudio ? 'Audio neu generieren' : 'Audio generieren'}
            </Button>
            {hasVoiceAudio && !audioPreviewUrl && (
              <span className="text-xs text-success-strong flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> Audio gespeichert
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Wird als Sprachansage abgespielt bevor der Anruf getrennt wird. Leer lassen für stille Trennung.
          </p>

          {audioPreviewUrl && (
            <div className="mt-2">
              <Label className="text-xs text-muted-foreground mb-1 block">Vorschau:</Label>
              <audio controls src={audioPreviewUrl} className="w-full h-10" />
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
            Erlaubte Ländervorwahlen
          </Label>
          <Input
            value={allowedPrefixesInput}
            onChange={(e) => setAllowedPrefixesInput(e.target.value)}
            placeholder="+49, +43"
            className="bg-muted border-border"
            disabled={loading}
          />
          <p className="text-xs text-muted-foreground">Erlaubt-Liste schränkt auf diese Länder ein.</p>
        </div>

        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
            Gesperrte Ländervorwahlen
          </Label>
          <Input
            value={blockedPrefixesInput}
            onChange={(e) => setBlockedPrefixesInput(e.target.value)}
            placeholder="+49, +43"
            className="bg-muted border-border"
            disabled={loading}
          />
          <p className="text-xs text-muted-foreground">Blockliste überschreibt immer.</p>
        </div>
      </div>

      <div className="space-y-3">
        <Label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
          Blockliste (Nummern)
        </Label>
        <div className="flex flex-col md:flex-row gap-2">
          <Input
            value={newBlockedNumber}
            onChange={(e) => setNewBlockedNumber(e.target.value)}
            placeholder="+491701234567"
            className="bg-muted border-border"
            disabled={loading}
          />
          <Button
            onClick={handleAddBlockedNumber}
            disabled={saving || loading || !newBlockedNumber.trim()}
            className="bg-[#1967D2] hover:bg-[#1558b0]"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Nummer blockieren
          </Button>
        </div>

        {blockedNumbers.length > 0 ? (
          <div className="space-y-2">
            {blockedNumbers.map((entry) => (
              <div
                key={entry.id}
                className="flex flex-col md:flex-row md:items-center justify-between gap-2 p-3 bg-muted border border-border rounded-md"
              >
                <div className="flex flex-col">
                  <span className="font-mono text-sm text-muted-foreground">{entry.number}</span>
                  <span className="text-xs text-muted-foreground">Normalisiert: {entry.normalized_number}</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRemoveBlockedNumber(entry.id)}
                  disabled={saving}
                >
                  Entfernen
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">Keine Nummern blockiert.</p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 pt-2">
        <Button
          onClick={handleSave}
          disabled={saving || loading}
          className="bg-[#1967D2] hover:bg-[#1558b0]"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Regeln speichern
        </Button>
      </div>
    </div>
  );
};

export default AgentRulesSection;
